/**
 * Running Community Store - State management for running features
 * Follows the same pattern as useSocialStore.ts
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/services/supabase/client';
import { useAirQualityStore } from '@/stores/useAirQualityStore';
import type {
  RunningActivity,
  RunnerProfile,
  RunClub,
  RunChallenge,
  LeaderboardEntry,
  ActivityComment,
  ActivityType,
  BestRunningTime,
  RunningFeedFilters,
  CreateActivityData,
  WeeklyStats,
  AQISample,
  RoutePoint,
  RecordingResult,
  RunSnapshot,
} from '@/types/running';

interface RunningState {
  // Feed
  activityFeed: RunningActivity[];
  feedLoading: boolean;
  feedError: string | null;
  hasMore: boolean;

  // Current runner profile being viewed
  viewingRunner: RunnerProfile | null;
  runnerActivities: RunningActivity[];

  // Current activity detail
  currentActivity: RunningActivity | null;
  currentComments: ActivityComment[];

  // Clubs
  clubs: RunClub[];
  myClubs: RunClub[];
  currentClub: RunClub | null;

  // Challenges
  challenges: RunChallenge[];
  currentChallenge: RunChallenge | null;
  challengeLeaderboard: LeaderboardEntry[];

  // Best Running Times (Blurp.ie unique)
  bestRunningTimes: BestRunningTime[];

  // Recording state
  isRecording: boolean;
  recordingData: {
    route: RoutePoint[];
    startTime: number | null;
    distance: number;
    aqi_samples: AQISample[];
    snapshots: RunSnapshot[];
    activityType: ActivityType;
    isPaused: boolean;
    pausedDuration: number;
  };

  // Weekly Stats
  weeklyStats: WeeklyStats | null;

  // Actions - Feed
  fetchActivityFeed: (filters: RunningFeedFilters, page?: number) => Promise<void>;
  resetFeed: () => void;

  // Actions - Profile
  fetchRunnerProfile: (userId: string) => Promise<RunnerProfile | null>;
  fetchRunnerActivities: (userId: string) => Promise<void>;

  // Actions - Activity
  fetchActivity: (activityId: string) => Promise<void>;
  createActivity: (data: CreateActivityData) => Promise<RunningActivity>;
  deleteActivity: (activityId: string) => Promise<void>;

  // Actions - Social
  giveKudos: (activityId: string) => Promise<void>;
  removeKudos: (activityId: string) => Promise<void>;
  addComment: (activityId: string, content: string) => Promise<void>;
  fetchComments: (activityId: string) => Promise<void>;

  // Actions - Follow
  followRunner: (userId: string) => Promise<void>;
  unfollowRunner: (userId: string) => Promise<void>;

  // Actions - Clubs
  fetchClubs: () => Promise<void>;
  fetchMyClubs: () => Promise<void>;
  fetchClub: (clubId: string) => Promise<void>;
  createClub: (data: { name: string; description: string; city: string; country: string; preferred_aqi_threshold?: number }) => RunClub;
  joinClub: (clubId: string) => Promise<void>;
  leaveClub: (clubId: string) => Promise<void>;

  // Actions - Challenges
  fetchChallenges: () => Promise<void>;
  fetchChallenge: (challengeId: string) => Promise<void>;
  joinChallenge: (challengeId: string) => Promise<void>;
  fetchChallengeLeaderboard: (challengeId: string) => Promise<void>;

  // Actions - Best Running Times
  fetchBestRunningTimes: (lat: number, lng: number) => Promise<void>;

  // Actions - Recording
  startRecording: (activityType?: ActivityType) => void;
  stopRecording: () => RecordingResult | null;
  clearRecordingData: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  addRoutePoint: (point: RoutePoint) => void;
  addAQISample: (sample: AQISample) => void;
  captureSnapshot: () => void;

  // Actions - Stats
  fetchWeeklyStats: () => Promise<void>;
}

const PAGE_SIZE = 20;

// Helper to calculate pace
const calculatePace = (distanceMeters: number, durationSeconds: number): number => {
  if (distanceMeters === 0) return 0;
  return (durationSeconds / (distanceMeters / 1000));
};

// Helper to calculate AQI exposure score
const calculateExposureScore = (samples: AQISample[]): number => {
  if (samples.length === 0) return 0;
  const avgAqi = samples.reduce((sum, s) => sum + s.aqi, 0) / samples.length;
  return Math.min(100, Math.round(avgAqi));
};

export const useRunningStore = create<RunningState>()(
  persist(
    (set, get) => ({
  // Initial state
  activityFeed: [],
  feedLoading: false,
  feedError: null,
  hasMore: true,
  viewingRunner: null,
  runnerActivities: [],
  currentActivity: null,
  currentComments: [],
  clubs: [],
  myClubs: [],
  currentClub: null,
  challenges: [],
  currentChallenge: null,
  challengeLeaderboard: [],
  bestRunningTimes: [],
  isRecording: false,
  recordingData: {
    route: [],
    startTime: null,
    distance: 0,
    aqi_samples: [],
    snapshots: [],
    activityType: 'run',
    isPaused: false,
    pausedDuration: 0,
  },
  weeklyStats: null,

  // Fetch activity feed
  fetchActivityFeed: async (filters: RunningFeedFilters, page = 0) => {
    set({ feedLoading: true, feedError: null });

    try {
      let query = supabase
        .from('running_activities')
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name,
            avatar_url, runner_stats
          )
        `)
        .eq('visibility', 'public')
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      // Apply filters
      if (filters.activity_types && filters.activity_types.length > 0) {
        query = query.in('activity_type', filters.activity_types);
      }
      if (filters.min_distance) {
        query = query.gte('distance_meters', filters.min_distance);
      }
      if (filters.max_distance) {
        query = query.lte('distance_meters', filters.max_distance);
      }

      const { data, error } = await query;

      if (error) throw error;

      const activities = (data || []) as RunningActivity[];

      set({
        activityFeed: page === 0 ? activities : [...get().activityFeed, ...activities],
        hasMore: activities.length === PAGE_SIZE,
        feedLoading: false,
      });
    } catch (error) {
      set({ feedError: (error as Error).message, feedLoading: false });
    }
  },

  resetFeed: () => {
    set({ activityFeed: [], hasMore: true, feedError: null });
  },

  // Fetch runner profile
  fetchRunnerProfile: async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*, runner_stats')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const runnerProfile: RunnerProfile = {
        id: profile.id,
        username: profile.username,
        first_name: profile.first_name,
        last_name: profile.last_name,
        avatar_url: profile.avatar_url,
        bio: profile.bio,
        runner_since: profile.runner_stats?.runner_since || null,
        total_runs: profile.runner_stats?.total_runs || 0,
        total_distance_km: profile.runner_stats?.total_distance_km || 0,
        total_duration_hours: profile.runner_stats?.total_duration_hours || 0,
        total_elevation_meters: profile.runner_stats?.total_elevation_meters || 0,
        best_5k_seconds: profile.runner_stats?.best_5k_seconds || null,
        best_10k_seconds: profile.runner_stats?.best_10k_seconds || null,
        best_half_marathon_seconds: profile.runner_stats?.best_half_marathon_seconds || null,
        best_marathon_seconds: profile.runner_stats?.best_marathon_seconds || null,
        average_aqi_exposure: profile.runner_stats?.average_aqi_exposure || 0,
        cleanest_run_aqi: profile.runner_stats?.cleanest_run_aqi || null,
        total_scent_reports_while_running: profile.runner_stats?.total_scent_reports_while_running || 0,
        followers_count: profile.followers_count || 0,
        following_count: profile.following_count || 0,
        kudos_received: profile.runner_stats?.kudos_received || 0,
        badges: [],
        clubs: [],
        is_verified: profile.is_verified_reporter || false,
        is_pro: profile.subscription_tier !== 'free',
        reporter_id: profile.reporter_id,
      };

      set({ viewingRunner: runnerProfile });
      return runnerProfile;
    } catch (error) {
      console.error('Error fetching runner profile:', error);
      return null;
    }
  },

  fetchRunnerActivities: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('running_activities')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      set({ runnerActivities: (data || []) as RunningActivity[] });
    } catch (error) {
      console.error('Error fetching runner activities:', error);
    }
  },

  // Fetch single activity
  fetchActivity: async (activityId: string) => {
    try {
      const { data, error } = await supabase
        .from('running_activities')
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name,
            avatar_url, runner_stats
          )
        `)
        .eq('id', activityId)
        .single();

      if (error) throw error;

      set({ currentActivity: data as RunningActivity });
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  },

  // Create activity (async with proper error handling)
  createActivity: async (data: CreateActivityData): Promise<RunningActivity> => {
    const localId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Calculate metrics
    const pace = calculatePace(data.distance_meters, data.duration_seconds);
    const avgSpeed = data.distance_meters > 0
      ? (data.distance_meters / 1000) / (data.duration_seconds / 3600)
      : 0;

    // Calculate AQI data if samples provided
    let aqiData = null;
    if (data.aqi_samples && data.aqi_samples.length > 0) {
      const avgAqi = Math.round(data.aqi_samples.reduce((sum, s) => sum + s.aqi, 0) / data.aqi_samples.length);
      const minAqi = Math.min(...data.aqi_samples.map(s => s.aqi));
      const maxAqi = Math.max(...data.aqi_samples.map(s => s.aqi));

      aqiData = {
        average_aqi: avgAqi,
        min_aqi: minAqi,
        max_aqi: maxAqi,
        aqi_samples: data.aqi_samples,
        exposure_score: calculateExposureScore(data.aqi_samples),
        health_impact: avgAqi <= 50 ? 'minimal' : avgAqi <= 100 ? 'low' : avgAqi <= 150 ? 'moderate' : 'high',
        breathed_air_quality: avgAqi <= 50 ? 'excellent' : avgAqi <= 100 ? 'good' : avgAqi <= 150 ? 'moderate' : 'poor',
      } as const;
    }

    const localActivity: RunningActivity = {
      id: localId,
      user_id: 'current-user',
      title: data.title,
      description: data.description || null,
      activity_type: data.activity_type,
      route_polyline: data.route_polyline,
      start_location: data.start_location || null,
      end_location: data.end_location || null,
      distance_meters: data.distance_meters,
      duration_seconds: data.duration_seconds,
      moving_time_seconds: data.moving_time_seconds || data.duration_seconds,
      pace_seconds_per_km: pace,
      average_speed_kmh: avgSpeed,
      max_speed_kmh: null,
      elevation_gain_meters: data.elevation_gain_meters || 0,
      elevation_loss_meters: data.elevation_loss_meters || 0,
      min_elevation_meters: null,
      max_elevation_meters: null,
      average_heart_rate: data.average_heart_rate || null,
      max_heart_rate: data.max_heart_rate || null,
      average_cadence: null,
      calories_burned: data.calories_burned || null,
      aqi_data: aqiData,
      scent_reports: [],
      photos: (data.photos || []).map((url, idx) => ({
        id: `photo-${localId}-${idx}`,
        url,
        thumbnail_url: url,
        caption: null,
        km_mark: null,
        location: null,
      })),
      kudos_count: 0,
      comments_count: 0,
      is_kudos: false,
      device_name: null,
      recorded_with: 'app',
      visibility: data.visibility,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user: {
        id: 'current-user',
        username: data.display_name || 'Runner',
        first_name: data.display_name || null,
        last_name: null,
        avatar_url: null,
        bio: null,
        runner_since: null,
        total_runs: 0,
        total_distance_km: 0,
        total_duration_hours: 0,
        total_elevation_meters: 0,
        best_5k_seconds: null,
        best_10k_seconds: null,
        best_half_marathon_seconds: null,
        best_marathon_seconds: null,
        average_aqi_exposure: 0,
        cleanest_run_aqi: null,
        total_scent_reports_while_running: 0,
        followers_count: 0,
        following_count: 0,
        kudos_received: 0,
        badges: [],
        clubs: [],
        is_verified: false,
        is_pro: false,
        reporter_id: null,
      },
    };

    // Add to feed immediately (optimistic)
    set({ activityFeed: [localActivity, ...get().activityFeed] });

    // Save to database with proper await and error handling
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ activityFeed: get().activityFeed.filter(a => a.id !== localId) });
        throw new Error('You must be logged in to save activities');
      }

      const dbData = {
        user_id: user.id,
        title: data.title,
        description: data.description,
        activity_type: data.activity_type,
        route_polyline: data.route_polyline,
        start_location: data.start_location,
        end_location: data.end_location,
        distance_meters: data.distance_meters,
        duration_seconds: data.duration_seconds,
        moving_time_seconds: data.moving_time_seconds || data.duration_seconds,
        pace_seconds_per_km: pace,
        average_speed_kmh: avgSpeed,
        elevation_gain_meters: data.elevation_gain_meters || 0,
        elevation_loss_meters: data.elevation_loss_meters || 0,
        average_heart_rate: data.average_heart_rate,
        max_heart_rate: data.max_heart_rate,
        calories_burned: data.calories_burned,
        aqi_data: aqiData,
        photos: data.photos || [],
        visibility: data.visibility,
        recorded_with: 'app',
      };

      const { data: activity, error } = await supabase
        .from('running_activities')
        .insert(dbData)
        .select()
        .single();

      if (error) {
        set({ activityFeed: get().activityFeed.filter(a => a.id !== localId) });
        throw new Error(error.message || 'Failed to save activity to database');
      }

      // Replace optimistic entry with real DB entry
      set({
        activityFeed: get().activityFeed.map(a =>
          a.id === localId ? (activity as RunningActivity) : a
        ),
      });

      return activity as RunningActivity;
    } catch (error) {
      // Remove optimistic entry on any failure
      set({ activityFeed: get().activityFeed.filter(a => a.id !== localId) });
      throw error;
    }
  },

  deleteActivity: async (activityId: string) => {
    try {
      await supabase
        .from('running_activities')
        .delete()
        .eq('id', activityId);

      set({
        activityFeed: get().activityFeed.filter((a) => a.id !== activityId),
      });
    } catch (error) {
      console.error('Error deleting activity:', error);
    }
  },

  // Kudos (like likes in useSocialStore)
  giveKudos: async (activityId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('running_kudos')
        .insert({ user_id: user.id, activity_id: activityId });

      set({
        activityFeed: get().activityFeed.map((a) =>
          a.id === activityId
            ? { ...a, kudos_count: a.kudos_count + 1, is_kudos: true }
            : a
        ),
      });
    } catch (error) {
      console.error('Error giving kudos:', error);
    }
  },

  removeKudos: async (activityId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('running_kudos')
        .delete()
        .eq('user_id', user.id)
        .eq('activity_id', activityId);

      set({
        activityFeed: get().activityFeed.map((a) =>
          a.id === activityId
            ? { ...a, kudos_count: Math.max(0, a.kudos_count - 1), is_kudos: false }
            : a
        ),
      });
    } catch (error) {
      console.error('Error removing kudos:', error);
    }
  },

  addComment: async (activityId: string, content: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: comment, error } = await supabase
        .from('running_comments')
        .insert({ user_id: user.id, activity_id: activityId, content })
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name, avatar_url
          )
        `)
        .single();

      if (error) throw error;

      set({
        currentComments: [...get().currentComments, comment as ActivityComment],
        activityFeed: get().activityFeed.map((a) =>
          a.id === activityId
            ? { ...a, comments_count: a.comments_count + 1 }
            : a
        ),
      });
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  },

  fetchComments: async (activityId: string) => {
    try {
      const { data, error } = await supabase
        .from('running_comments')
        .select(`
          *,
          user:profiles!user_id (
            id, username, first_name, last_name, avatar_url
          )
        `)
        .eq('activity_id', activityId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      set({ currentComments: (data || []) as ActivityComment[] });
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  },

  // Follow/Unfollow (reuse existing follows table)
  followRunner: async (userId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('follows')
        .insert({ follower_id: user.id, following_id: userId });

      const runner = get().viewingRunner;
      if (runner && runner.id === userId) {
        set({
          viewingRunner: {
            ...runner,
            followers_count: runner.followers_count + 1,
            is_following: true,
          },
        });
      }
    } catch (error) {
      console.error('Error following runner:', error);
    }
  },

  unfollowRunner: async (userId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', userId);

      const runner = get().viewingRunner;
      if (runner && runner.id === userId) {
        set({
          viewingRunner: {
            ...runner,
            followers_count: Math.max(0, runner.followers_count - 1),
            is_following: false,
          },
        });
      }
    } catch (error) {
      console.error('Error unfollowing runner:', error);
    }
  },

  // Clubs
  fetchClubs: async () => {
    try {
      const { data, error } = await supabase
        .from('run_clubs')
        .select('*')
        .eq('club_type', 'public')
        .order('members_count', { ascending: false })
        .limit(50);

      if (error) throw error;

      set({ clubs: (data || []) as RunClub[] });
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  },

  fetchMyClubs: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('club_members')
        .select(`
          club:run_clubs (*)
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const clubs = (data || []).map((m: unknown) => (m as { club: RunClub }).club);
      set({ myClubs: clubs });
    } catch (error) {
      console.error('Error fetching my clubs:', error);
    }
  },

  fetchClub: async (clubId: string) => {
    try {
      const { data, error } = await supabase
        .from('run_clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (error) throw error;

      set({ currentClub: data as RunClub });
    } catch (error) {
      console.error('Error fetching club:', error);
    }
  },

  // Create club (optimistic update)
  createClub: (data: { name: string; description: string; city: string; country: string; preferred_aqi_threshold?: number }): RunClub => {
    const localId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const localClub: RunClub = {
      id: localId,
      name: data.name,
      description: data.description,
      city: data.city,
      country: data.country,
      logo_url: null,
      cover_image_url: null,
      meeting_point: null,
      club_type: 'public',
      sport_types: ['run'],
      regular_run_schedule: [],
      owner_id: 'current-user',
      admins: ['current-user'],
      members_count: 1,
      total_activities: 0,
      total_distance_km: 0,
      preferred_aqi_threshold: data.preferred_aqi_threshold || 100,
      average_member_aqi_exposure: 0,
      is_member: true,
      membership_status: 'admin',
      created_at: new Date().toISOString(),
    };

    // Add to clubs immediately
    set({
      clubs: [localClub, ...get().clubs],
      myClubs: [localClub, ...get().myClubs],
    });

    // Save to database in background
    setTimeout(() => {
      supabase.auth.getUser().then(({ data: { user } }) => {
        supabase
          .from('run_clubs')
          .insert({
            name: data.name,
            description: data.description,
            city: data.city,
            country: data.country,
            club_type: 'public',
            preferred_aqi_threshold: data.preferred_aqi_threshold,
            created_by: user?.id,
          })
          .select()
          .single()
          .then(({ data: club, error }) => {
            if (!error && club) {
              // Update local state with real ID
              set({
                clubs: get().clubs.map((c) =>
                  c.id === localId ? (club as RunClub) : c
                ),
                myClubs: get().myClubs.map((c) =>
                  c.id === localId ? (club as RunClub) : c
                ),
              });
              // Also add user as admin member
              if (user) {
                supabase
                  .from('club_members')
                  .insert({ club_id: club.id, user_id: user.id, role: 'admin' });
              }
            }
          });
      });
    }, 0);

    return localClub;
  },

  joinClub: async (clubId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('club_members')
        .insert({ club_id: clubId, user_id: user.id, role: 'member' });

      // Update local state
      const club = get().currentClub;
      if (club && club.id === clubId) {
        set({
          currentClub: {
            ...club,
            members_count: club.members_count + 1,
            is_member: true,
            membership_status: 'member',
          },
        });
      }
    } catch (error) {
      console.error('Error joining club:', error);
    }
  },

  leaveClub: async (clubId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('club_members')
        .delete()
        .eq('club_id', clubId)
        .eq('user_id', user.id);

      const club = get().currentClub;
      if (club && club.id === clubId) {
        set({
          currentClub: {
            ...club,
            members_count: Math.max(0, club.members_count - 1),
            is_member: false,
            membership_status: 'none',
          },
        });
      }
    } catch (error) {
      console.error('Error leaving club:', error);
    }
  },

  // Challenges
  fetchChallenges: async () => {
    try {
      const { data, error } = await supabase
        .from('run_challenges')
        .select('*')
        .gte('end_date', new Date().toISOString())
        .order('participants_count', { ascending: false });

      if (error) throw error;

      set({ challenges: (data || []) as RunChallenge[] });
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  },

  fetchChallenge: async (challengeId: string) => {
    try {
      const { data, error } = await supabase
        .from('run_challenges')
        .select('*')
        .eq('id', challengeId)
        .single();

      if (error) throw error;

      set({ currentChallenge: data as RunChallenge });
    } catch (error) {
      console.error('Error fetching challenge:', error);
    }
  },

  joinChallenge: async (challengeId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('challenge_participants')
        .insert({ challenge_id: challengeId, user_id: user.id });

      const challenge = get().currentChallenge;
      if (challenge && challenge.id === challengeId) {
        set({
          currentChallenge: {
            ...challenge,
            participants_count: challenge.participants_count + 1,
            is_participating: true,
          },
        });
      }
    } catch (error) {
      console.error('Error joining challenge:', error);
    }
  },

  fetchChallengeLeaderboard: async (challengeId: string) => {
    try {
      const { data, error } = await supabase
        .from('challenge_participants')
        .select(`
          progress,
          user:profiles!user_id (
            id, username, first_name, last_name, avatar_url, runner_stats
          )
        `)
        .eq('challenge_id', challengeId)
        .order('progress', { ascending: false })
        .limit(50);

      if (error) throw error;

      const leaderboard: LeaderboardEntry[] = (data || []).map((entry, index) => ({
        rank: index + 1,
        user: entry.user as unknown as RunnerProfile,
        value: entry.progress,
        change_from_yesterday: 0,
      }));

      set({ challengeLeaderboard: leaderboard });
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  },

  // Best Running Times (Blurp.ie unique - uses AQ forecast)
  fetchBestRunningTimes: async (_lat: number, _lng: number) => {
    // This would integrate with the air quality API
    // For now, generate mock recommendations based on time of day
    const times: BestRunningTime[] = [];
    const currentHour = new Date().getHours();

    for (let i = 0; i < 24; i++) {
      const hour = (currentHour + i) % 24;
      const timeSlot = `${hour.toString().padStart(2, '0')}:00`;

      // Morning and evening are generally better for running
      const isMorning = hour >= 5 && hour <= 8;
      const isEvening = hour >= 17 && hour <= 20;
      const isMidday = hour >= 11 && hour <= 15;

      let predictedAqi = 50;
      let temperature = 25;
      let recommendation: 'excellent' | 'good' | 'moderate' | 'avoid' = 'good';
      let reason = '';

      if (isMorning) {
        predictedAqi = 35;
        temperature = 22;
        recommendation = 'excellent';
        reason = 'Low pollution, cool temperature';
      } else if (isEvening) {
        predictedAqi = 45;
        temperature = 26;
        recommendation = 'good';
        reason = 'Moderate conditions, cooling down';
      } else if (isMidday) {
        predictedAqi = 75;
        temperature = 32;
        recommendation = 'avoid';
        reason = 'High UV, peak heat';
      } else {
        predictedAqi = 55;
        temperature = 24;
        recommendation = 'moderate';
        reason = 'Acceptable conditions';
      }

      times.push({
        time_slot: timeSlot,
        predicted_aqi: predictedAqi,
        temperature,
        humidity: 65,
        uv_index: isMidday ? 8 : isMorning ? 3 : 2,
        recommendation_score: recommendation === 'excellent' ? 95 : recommendation === 'good' ? 75 : recommendation === 'moderate' ? 50 : 25,
        recommendation,
        reason,
      });
    }

    set({ bestRunningTimes: times });
  },

  // Recording actions
  startRecording: (activityType?: ActivityType) => {
    set({
      isRecording: true,
      recordingData: {
        route: [],
        startTime: Date.now(),
        distance: 0,
        aqi_samples: [],
        snapshots: [],
        activityType: activityType || 'run',
        isPaused: false,
        pausedDuration: 0,
      },
    });
  },

  stopRecording: () => {
    const { recordingData } = get();
    if (!recordingData.startTime) return null;

    const totalElapsed = Date.now() - recordingData.startTime;
    const activeDuration = Math.floor((totalElapsed - recordingData.pausedDuration) / 1000);

    // Calculate elevation gain from route points
    let elevationGain = 0;
    let elevationLoss = 0;
    let maxSpeed = 0;
    for (let i = 1; i < recordingData.route.length; i++) {
      const prev = recordingData.route[i - 1];
      const curr = recordingData.route[i];
      if (prev.elevation != null && curr.elevation != null) {
        const diff = curr.elevation - prev.elevation;
        if (diff > 0) elevationGain += diff;
        else elevationLoss += Math.abs(diff);
      }
    }

    // Stop recording but KEEP the data until save succeeds
    set({ isRecording: false });

    // Return data matching RecordingResult type
    return {
      route: recordingData.route,
      duration: activeDuration,
      distance: recordingData.distance,
      aqi_samples: recordingData.aqi_samples,
      snapshots: recordingData.snapshots,
      activityType: recordingData.activityType,
      elevationGain,
      elevationLoss,
      maxSpeed,
    } as RecordingResult;
  },

  clearRecordingData: () => {
    set({
      recordingData: {
        route: [],
        startTime: null,
        distance: 0,
        aqi_samples: [],
        snapshots: [],
        activityType: 'run',
        isPaused: false,
        pausedDuration: 0,
      },
    });
  },

  pauseRecording: () => {
    set({
      recordingData: {
        ...get().recordingData,
        isPaused: true,
      },
    });
  },

  resumeRecording: () => {
    set({
      recordingData: {
        ...get().recordingData,
        isPaused: false,
      },
    });
  },

  addRoutePoint: (point: RoutePoint) => {
    const { recordingData } = get();
    console.log('[STORE] addRoutePoint called, current route length:', recordingData.route.length, 'current distance:', recordingData.distance);
    const prevPoint = recordingData.route[recordingData.route.length - 1];

    let additionalDistance = 0;
    if (prevPoint) {
      console.log('[STORE] Previous point:', prevPoint.lat, prevPoint.lng);
      console.log('[STORE] New point:', point.lat, point.lng);
      // Calculate distance using Haversine formula
      const R = 6371e3; // Earth's radius in meters
      const lat1 = prevPoint.lat * Math.PI / 180;
      const lat2 = point.lat * Math.PI / 180;
      const deltaLat = (point.lat - prevPoint.lat) * Math.PI / 180;
      const deltaLng = (point.lng - prevPoint.lng) * Math.PI / 180;

      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      additionalDistance = R * c;
      console.log('[STORE] Calculated additional distance:', additionalDistance, 'meters');
    } else {
      console.log('[STORE] First point, no previous point to calculate distance from');
    }

    const newDistance = recordingData.distance + additionalDistance;
    console.log('[STORE] New total distance:', newDistance, 'meters');

    set({
      recordingData: {
        ...recordingData,
        route: [...recordingData.route, point],
        distance: newDistance,
      },
    });

    console.log('[STORE] Route updated, new length:', recordingData.route.length + 1);
  },

  addAQISample: (sample: AQISample) => {
    const { recordingData } = get();
    set({
      recordingData: {
        ...recordingData,
        aqi_samples: [...recordingData.aqi_samples, sample],
      },
    });
  },

  captureSnapshot: () => {
    const { recordingData } = get();
    if (!recordingData.startTime) return;

    const now = Date.now();
    const totalElapsed = now - recordingData.startTime;
    const elapsed = Math.floor((totalElapsed - recordingData.pausedDuration) / 1000);
    const distKm = recordingData.distance / 1000;
    const pace = distKm > 0.01 ? elapsed / distKm : 0;

    const lastSample = recordingData.aqi_samples[recordingData.aqi_samples.length - 1];
    const lastPoint = recordingData.route[recordingData.route.length - 1];
    const airQuality = useAirQualityStore.getState().currentReading;

    const snapshot: RunSnapshot = {
      timestamp: new Date().toISOString(),
      elapsed,
      distance: recordingData.distance,
      pace,
      aqi: airQuality?.aqi || lastSample?.aqi || 0,
      pm25: lastSample?.pm25 || null,
      temperature: lastSample?.temperature || null,
      humidity: lastSample?.humidity || null,
      uv_index: lastSample?.uv_index || null,
      lat: lastPoint?.lat || 0,
      lng: lastPoint?.lng || 0,
    };

    set({
      recordingData: {
        ...recordingData,
        snapshots: [...recordingData.snapshots, snapshot],
      },
    });
  },

  // Weekly stats
  fetchWeeklyStats: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('running_activities')
        .select('distance_meters, duration_seconds, elevation_gain_meters, aqi_data, pace_seconds_per_km')
        .eq('user_id', user.id)
        .gte('created_at', oneWeekAgo.toISOString());

      if (error) throw error;

      const activities = data || [];

      const stats: WeeklyStats = {
        totalDistance: activities.reduce((sum, a) => sum + (a.distance_meters || 0), 0) / 1000,
        totalDuration: activities.reduce((sum, a) => sum + (a.duration_seconds || 0), 0) / 3600,
        totalRuns: activities.length,
        totalElevation: activities.reduce((sum, a) => sum + (a.elevation_gain_meters || 0), 0),
        averageAQI: activities.length > 0
          ? activities.reduce((sum, a) => sum + (a.aqi_data?.average_aqi || 0), 0) / activities.length
          : 0,
        comparedToLastWeek: 0, // Would need another query for comparison
        longestRun: Math.max(...activities.map(a => a.distance_meters || 0)) / 1000,
        fastestPace: Math.min(...activities.filter(a => a.pace_seconds_per_km).map(a => a.pace_seconds_per_km)),
      };

      set({ weeklyStats: stats });
    } catch (error) {
      console.error('Error fetching weekly stats:', error);
    }
  },
    }),
    {
      name: 'blurp-running-store',
      // Persist feed, runner activities, and recording data (crash protection)
      partialize: (state) => ({
        activityFeed: state.activityFeed.slice(0, 50), // Keep last 50 activities
        runnerActivities: state.runnerActivities.slice(0, 50),
        weeklyStats: state.weeklyStats,
        isRecording: state.isRecording,
        recordingData: state.recordingData,
      }),
    }
  )
);

// Selectors
export const selectActivityFeed = (state: RunningState) => state.activityFeed;
export const selectIsRecording = (state: RunningState) => state.isRecording;
export const selectWeeklyStats = (state: RunningState) => state.weeklyStats;
export const selectBestRunningTimes = (state: RunningState) => state.bestRunningTimes;
